import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Account } from '~/lib/types/basket-types';
import useBasketSection from '~/hooks/use-basket-section';
import { DEBOUNCE_TIME, EDGE_API } from '~/lib/constants';
import { BasketSections } from '~/lib/types/enums';
import SenderType from '~/components/parcel-details/sender-type/sender-type';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import useSWR from 'swr';
import { SenderTypeResponse } from '~/lib/types/edge-api-types';
import { SenderTypeOptions } from '~/components/parcel-details/sender-type/sender-type-types';
import { isEmail } from '@parcel2go/core-ui';
import { fetchWithAnalytics } from '~/lib/fetch';
import { capturePromiseException } from '~/lib/metrics';
import useWorkflowActions from '~/hooks/use-workflow-actions';
import { BaseModuleProps } from '~/modules/module-factory';
import { PublicSettings } from '~/lib/ui-serverless-settings';

const SenderTypeCore: FC<BaseModuleProps> = ({ errors, wrapper }) => {
  const { settings } = useTenant();
  const { addCompletedAction, storeWorkflowData, workflowData } = useWorkflowActions();
  const account = useBasketSection<Account>({ name: BasketSections.ACCOUNT });
  const { track, session } = useAnalytics();
  const [emailState, setEmail] = useState<string | undefined>(
    account?.data?.email?.length ? account.data.email : undefined
  );
  const { tenant } = useTenant();

  useEffect(() => {
    if (account.data.email && account.data.email.length > 0 && emailState !== account.data.email) {
      const t = setTimeout(() => setEmail(account.data.email), DEBOUNCE_TIME);
      return () => clearTimeout(t);
    }
  }, [account, emailState]);

  const onSelect = useCallback(
    (type: SenderTypeOptions) => {
      addCompletedAction('SENDER-TYPE');
      storeWorkflowData('SENDER-TYPE', type);
      track({ type: 'checkout_action', payload: { action: 'sender_type_changed', type: type } });
    },
    [addCompletedAction, storeWorkflowData, track]
  );

  const value = useMemo(() => {
    const v = workflowData.get('SENDER-TYPE');
    if (typeof v === 'string') {
      return v;
    }
  }, [workflowData]);

  const { data, isLoading } = useSWR<SenderTypeResponse>(
    settings[PublicSettings.ORDERPROCESS.SENDERTYPEENABLED] === 'true' && isEmail(emailState || '')
      ? `sendertype-${emailState}`
      : undefined,
    () => {
      return fetchWithAnalytics(session, `${EDGE_API}/${tenant}/order-process/sender-type`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: emailState })
      })
        .then((res) => res.json())
        .catch(capturePromiseException);
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false, revalidateIfStale: true }
  );

  if (isLoading || !data || !data.success || !data.result?.display || !account || !isEmail(emailState || '')) {
    return null;
  }

  return wrapper(<SenderType value={value} onSelect={onSelect} errors={errors} />);
};
export default SenderTypeCore;
